<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { getGoodsList } from '/api/index.js'

const props = defineProps({
    categoryData: {
        type: Array,
        default: () => [],
    },
})

const activeCategoryId = ref(null)
const goodsMap = ref(new Map())
const loading = ref(false)

const backgroundLeft = ref(0)
const backgroundWidth = ref(0)
const backgroundHeight = ref(0)

const containerRef = ref(null)

const currentGoodsList = computed(() => {
    return goodsMap.value.get(Number(activeCategoryId.value)) || []
})

const initActiveCategory = () => {
    if (!props.categoryData.length) return

    const exists = props.categoryData.some(item => item.id === activeCategoryId.value)

    if (!activeCategoryId.value || !exists) {
        activeCategoryId.value = props.categoryData[0].id
    }
}

const updateBackgroundBlock = async (target) => {
    await nextTick()

    let el = target

    if (!el) {
        el = containerRef.value?.querySelector('.Heziyun-itemButton.Heziyun-select')
    }

    if (!el) return

    backgroundLeft.value = el.offsetLeft
    backgroundWidth.value = el.offsetWidth
    backgroundHeight.value = el.offsetHeight
}

const fetchGoodsList = async () => {
    loading.value = true

    try {
        const res = await getGoodsList({
            limit: 1000,
            orderby: 'id',
            sort: 'asc',
        })

        const list = Array.isArray(res?.data?.list) ? res.data.list : []
        const map = new Map()

        for (const item of list) {
            const typeId = Number(item.typeid)

            if (!map.has(typeId)) {
                map.set(typeId, [])
            }

            map.get(typeId).push(item)
        }

        goodsMap.value = map
    } catch (error) {
        console.error('获取商品列表失败:', error)
        goodsMap.value = new Map()
    } finally {
        loading.value = false
    }
}

const productSwitching = async (id, event) => {
    if (activeCategoryId.value === id) return

    activeCategoryId.value = id

    const target = event?.currentTarget

    await updateBackgroundBlock(target)

    target?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
    })
}

const handleResize = () => {
    updateBackgroundBlock()
}

watch(
    () => props.categoryData,
    async () => {
        initActiveCategory()
        await updateBackgroundBlock()
    },
    {
        immediate: true,
        deep: true,
    }
)

onMounted(async () => {
    initActiveCategory()
    await fetchGoodsList()
    await updateBackgroundBlock()

    window.addEventListener('resize', handleResize, { passive: true })
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
})
</script>

<template>
    <div class="Heziyun-commodity Heziyun-padding">
        <div class="Heziyun-defaultWidth">
            <div class="Heziyun-commodity-title">
                <span v-intersect="{ class: 'bottom-top', delay: 0 }">
                    {{ $t('index.shop1') }}
                </span>
                <p v-intersect="{ class: 'bottom-top', delay: 100 }">
                    {{ $t('index.shop2') }}
                </p>
            </div>

            <div class="Heziyun-includes" v-intersect="{ class: 'bottom-top', delay: 200 }">
                <div class="Heziyun-switchBar" ref="containerRef">
                    <div class="Heziyun-labelColumn">
                        <div v-for="item in categoryData" :key="item.id" class="Heziyun-itemButton"
                            :class="{ 'Heziyun-select': item.id === activeCategoryId }"
                            @click="productSwitching(item.id, $event)">
                            {{ item.title }}
                        </div>

                        <div v-show="backgroundWidth > 0" class="Heziyun-backgroundBlock" :style="{
                            left: `${backgroundLeft}px`,
                            width: `${backgroundWidth}px`,
                            height: `${backgroundHeight}px`,
                        }" />
                    </div>

                    <div class="Heziyun-arrow" v-if="categoryData.length > 7">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>

                    <div class="Heziyun-separateButton">
                        <a href="/cart" class="Heziyun-whole">
                            <img src="@/static/img/index/whole.png" alt="" />
                            {{ $t('index.viewAll') }}
                        </a>
                    </div>
                </div>

                <div class="Heziyun-height">
                    <a-spin :loading="loading" tip="加载中...">
                        <div class="Heziyun-primaryCoverage" v-if="currentGoodsList.length" :key="activeCategoryId">
                            <div class="Heziyun-itemServer" v-for="(item, index) in currentGoodsList"
                                :key="`${activeCategoryId}-${item.id}`"
                                v-intersect="{ class: 'opacity', delay: index * 10 }">
                                <div class="Heziyun-title">{{ item.title }}</div>
                                <div class="Heziyun-describe">{{ item.tips }}</div>

                                <div class="Heziyun-toConfigure">
                                    <div class="Heziyun-setUp">
                                        <span>CPU</span>
                                        <p>{{ item.cpu }}{{ $t('index.nucleus') }}</p>
                                    </div>
                                    <div class="Heziyun-setUp">
                                        <span>{{ $t('index.memory') }}</span>
                                        <p>{{ item.ram }}G</p>
                                    </div>
                                    <div class="Heziyun-setUp">
                                        <span>{{ $t('index.systemDisk') }}</span>
                                        <p>{{ item.rom }}G</p>
                                    </div>
                                    <div class="Heziyun-setUp">
                                        <span>{{ $t('index.dataDisk') }}</span>
                                        <p>{{ item.data_rom }}G</p>
                                    </div>
                                    <div class="Heziyun-setUp">
                                        <span>{{ $t('index.bandwidth') }}</span>
                                        <p>{{ item.internet }}M</p>
                                    </div>
                                    <div class="Heziyun-setUp">
                                        <span>{{ $t('index.defense') }}</span>
                                        <p>{{ item.defense }}G</p>
                                    </div>
                                </div>

                                <div class="Heziyun-amountOfMoney">
                                    <div class="Heziyun-price">
                                        <i>¥{{ item.money }}</i>/{{ $t('index.month') }}
                                    </div>
                                    <a :href="item.url">{{ $t('index.buyNow') }}</a>
                                </div>
                            </div>
                        </div>

                        <div class="Heziyun-noDataAvailable" v-else>
                            <img src="@/static/img/index/wuData.png" alt="" />
                            <span>{{ $t('index.noDataAvailable') }}</span>
                        </div>
                    </a-spin>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less" scoped>
:deep(.arco-spin) {
    width: 100%;
}

.Heziyun-commodity {
    padding-top: 8rem;

    .Heziyun-defaultWidth {
        .vs-loading--target {
            background-color: rgb(214, 214, 214);
        }

        .Heziyun-switchBar {
            height: 3.5rem;
            border-radius: 0.5rem 0.5rem 0 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: relative;
            background:
                linear-gradient(0deg, rgba(0, 0, 0, 0.001), rgba(0, 0, 0, 0.001)),
                linear-gradient(135deg, #4d8bff 0%, #2d5fff 100%);

            .Heziyun-labelColumn {
                width: 75%;
                padding: 0 2rem;
                display: flex;
                align-items: center;
                gap: 1rem;
                overflow-x: auto;
                position: relative;
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    display: none;
                }

                .Heziyun-itemButton {
                    padding: 0.35rem 1rem;
                    border-radius: 2rem;
                    cursor: pointer;
                    color: #fff;
                    flex-shrink: 0;
                    position: relative;
                    z-index: 1;
                    transition: color 0.2s ease, background 0.2s ease;

                    &:hover {
                        background: #ffffff52;
                    }
                }

                .Heziyun-select {
                    color: #1f2937;
                    font-weight: bold;
                }

                .Heziyun-backgroundBlock {
                    position: absolute;
                    border-radius: 2rem;
                    background:
                        linear-gradient(0deg, rgba(0, 0, 0, 0.001), rgba(0, 0, 0, 0.001)),
                        linear-gradient(135deg, rgba(255, 255, 255, 0.95) 3%, rgba(255, 255, 255, 0.85) 99%);
                    box-shadow: 0 0.25rem 0.94rem rgba(49, 118, 255, 0.2);
                    transition: left 0.25s ease, width 0.25s ease, height 0.25s ease;
                }
            }

            .Heziyun-arrow {
                transform: rotate(90deg);
                cursor: pointer;
                position: absolute;
                right: 16rem;
                top: 2.1rem;

                span {
                    display: block;
                    width: 0.8rem;
                    height: 0.8rem;
                    border-top: 0.25rem solid #fff;
                    border-left: 0.25rem solid #fff;
                    transform: rotate(45deg);
                    margin: -0.8rem;
                    animation: arrowMove 2s infinite;
                }

                span:nth-of-type(2) {
                    animation-delay: -0.2s;
                }

                span:nth-of-type(3) {
                    animation-delay: -0.4s;
                }
            }

            .Heziyun-separateButton {
                width: 13%;
                padding-right: 2rem;

                .Heziyun-whole {
                    padding: 0.35rem 1rem;
                    border-radius: 2rem;
                    cursor: pointer;
                    color: #fff;
                    background: rgba(255, 255, 255, 0.2);
                    box-shadow: 0 0.25rem 0.94rem rgba(49, 118, 255, 0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;

                    &:hover {
                        background: rgba(255, 255, 255, 0.4);
                    }

                    img {
                        width: 1.2rem;
                        height: 1.2rem;
                    }
                }
            }
        }

        .Heziyun-height {
            max-height: 47.2rem;
            box-shadow: inset 8px 8px 20px 0 rgba(55, 99, 170, 0.11), inset -8px -8px 20px 0 #fff;
            border-radius: 0 0 0.5rem 0.5rem;
            overflow-x: hidden;
            background-color: #f3f5f8;

            &::-webkit-scrollbar {
                width: 0.6rem;
                background-color: #f3f5f8;
            }

            &::-webkit-scrollbar-thumb {
                background-color: #3176ff;
                border-radius: 1rem;
                border: 2px solid #f3f5f8;

                &:hover {
                    background-color: #f3f5f8;
                }
            }

            &::-webkit-scrollbar-track {
                background-color: #f3f5f8;
                border-radius: 6px;
            }

            &::-webkit-scrollbar-button {
                display: none;
            }

            .Heziyun-noDataAvailable {
                padding: 13rem 0;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                position: relative;
            }
        }

        .Heziyun-primaryCoverage {
            position: relative;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            padding: 2rem;
            gap: 0.1rem;
            animation: opacity 0.5s ease;

            .Heziyun-itemServer {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 0.8rem;
                width: calc(25% - 0.1rem);
                max-height: 17.3rem;
                flex-shrink: 0;
                padding: 1rem;
                background: #fff;
                transition: box-shadow 0.4s ease;

                &:hover {
                    z-index: 1;
                    box-shadow: 3px 3px 12px rgba(55, 99, 170, 0.2);
                }

                .Heziyun-title {
                    font-size: 1rem;
                }

                .Heziyun-describe {
                    color: #6b7280;
                    font-size: 0.7rem;
                }

                .Heziyun-toConfigure {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 0.4rem;

                    .Heziyun-setUp {
                        width: calc(50% - 0.2rem);
                        flex-shrink: 0;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background: #f9fafb;
                        padding: 0.3rem;

                        span {
                            color: #4b5563;
                            font-size: 0.7rem;
                        }

                        p {
                            color: #3176ff;
                            font-size: 0.75rem;
                            font-weight: bold;
                        }
                    }
                }

                .Heziyun-amountOfMoney {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    .Heziyun-price {
                        color: #6b7280;
                        font-size: 0.7rem;

                        i {
                            font-style: normal;
                            color: #3176ff;
                            font-size: 1.2rem;
                            font-weight: bold;
                        }
                    }

                    a {
                        background: #3176ff;
                        color: #fff;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 5rem;
                        height: 1.9rem;
                        border-radius: 0.2rem;
                        box-shadow: 0 4px 16px rgba(11, 83, 255, 0.24);

                        &:hover {
                            opacity: 0.75;
                        }
                    }
                }
            }
        }
    }
}

@keyframes arrowMove {
    0% {
        opacity: 0;
        transform: rotate(45deg) translate(20px, 20px);
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform: rotate(45deg) translate(-20px, -20px);
    }
}

@media (max-width: 768px) {
    .Heziyun-commodity {
        padding-top: 5vw;

        .Heziyun-defaultWidth {
            .Heziyun-switchBar {
                height: 12vw;
                border-radius: 2vw 2vw 0 0;

                .Heziyun-labelColumn {
                    width: 100%;
                    padding: 0 3vw;
                    gap: 2vw;

                    .Heziyun-itemButton {
                        padding: 1vw 3vw;
                        border-radius: 20vw;
                        font-size: 3.5vw;
                    }

                    .Heziyun-backgroundBlock {
                        border-radius: 10vw;
                    }
                }

                .Heziyun-arrow,
                .Heziyun-separateButton {
                    display: none;
                }
            }

            .Heziyun-height {
                max-height: 200vw;

                &::-webkit-scrollbar {
                    width: 0;
                }

                .Heziyun-noDataAvailable {
                    padding: 30vw 0;

                    span {
                        font-size: 4vw;
                    }
                }
            }

            .Heziyun-primaryCoverage {
                padding: 3vw;
                gap: 0.3vw;
                flex-wrap: initial;
                overflow-x: auto;
                &::-webkit-scrollbar {
                    display: none;
                }

                .Heziyun-itemServer {
                    width: 100%;
                    max-height: 65vw;
                    gap: 3vw;
                    padding: 3vw;

                    .Heziyun-title {
                        font-size: 4.5vw;
                    }

                    .Heziyun-describe {
                        font-size: 3.5vw;
                    }

                    .Heziyun-toConfigure {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 0.5vw;
                        width: 100%;

                        .Heziyun-setUp {
                            width: auto;
                            padding: 2vw;

                            span,
                            p {
                                font-size: 3.5vw;
                            }
                        }
                    }

                    .Heziyun-amountOfMoney {
                        .Heziyun-price {
                            font-size: 3.5vw;

                            i {
                                font-size: 5vw;
                            }
                        }

                        a {
                            width: 25vw;
                            height: 9vw;
                            font-size: 3.5vw;
                            border-radius: 1vw;
                        }
                    }
                }
            }
        }
    }
}
</style>